import {makeAutoObservable} from 'mobx';
import {ViewController} from 'data/types/structure';
import {inject, injectable} from 'inversify';
import {Bindings} from 'data/constants/bindings';
import type {ILocalizationStore} from 'data/stores/localization/localization.store';
import type {IHeavyPredictionsStore} from 'data/stores/heavy_predictions/heavy_predictions.store';
import type {ITournamentsStore} from 'data/stores/tournaments/tournaments.store';
import type {IRoundsStore} from 'data/stores/rounds/rounds.store';
import type {IStandingsStore} from 'data/stores/standings/standings.store';
import {every, get, groupBy, includes, map} from 'lodash';
import type {IModalsStore} from 'data/stores/modals/modals.store';
import {ModalType, RoundStatus} from 'data/enums';
import type {IUserStore} from 'data/stores/user/user.store';
import type {IMatchFilterStore} from 'data/stores/match_filter_store/match_filter_store.store';
import {ConnextraType, createConnextraScript} from 'data/utils/createConnextraScript';

export interface IControllBarController extends ViewController {
	readonly i18n: ILocalizationStore;

	get isSaveVisible(): boolean;
	get isResetVisible(): boolean;
	get isAutoFillVisible(): boolean;
	get isAutoFillDisabled(): boolean;
	get isAllPlayoffPicked(): boolean;
	get isLockout(): boolean;

	onResetPicks: () => void;
	onSavePredictions: () => void;
	onAutoFill: () => void;
	onSavePlayoffPicture: () => void;
}

@injectable()
export class ControllBarController implements IControllBarController {
	private get matchesByRoundId() {
		return this._tournamentStore.tournaments.filter(
			(tournament) => tournament.roundId === this._roundStore.selectedRoundId
		);
	}

	get isSaveVisible() {
		return every([this._hpStore.isPredictionsChanged]);
	}

	get isResetVisible() {
		return this._hpStore.predictionBySelectedRoundId.length > 0;
	}

	get isAutoFillDisabled() {
		if (this._roundStore.selectedRound?.status === RoundStatus.Complete) {
			return true;
		}

		return this._hpStore.predictionBySelectedRoundId.length === this.matchesByRoundId.length;
	}

	get isAutoFillVisible() {
		const filters = this._matchFilterStore.filter;
		return !(filters.division !== "0" || filters.squad !== "0");
	}

	get isAllPlayoffPicked() {
		const playOffTournamentsObj = groupBy(
			this._tournamentStore.availablePlayoffTournaments,
			"roundId"
		);
		const predictionsForTournamentsArr = map(playOffTournamentsObj, (tournament, roundId) => {
			return tournament.length === this._hpStore.predictions[Number(roundId)]?.length;
		});

		return predictionsForTournamentsArr.every(Boolean);
	}

	get isLockout() {
		return this._roundStore.isRoundsLockout;
	}

	constructor(
		@inject(Bindings.LocalizationStore) readonly i18n: ILocalizationStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.HeavyPredictionsStore) private _hpStore: IHeavyPredictionsStore,
		@inject(Bindings.TournamentsStore) private _tournamentStore: ITournamentsStore,
		@inject(Bindings.RoundsStore) private _roundStore: IRoundsStore,
		@inject(Bindings.StandingsStore) private _standingsStore: IStandingsStore,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore,
		@inject(Bindings.MatchFilterStore) private _matchFilterStore: IMatchFilterStore
	) {
		makeAutoObservable(this);
	}

	onResetPicks = () => {
		this._hpStore.resetPredictionForRound();

		this._hpStore.setIsPredictionsChanged(false);
		this._hpStore.setIsPredictionsSaved(false);

		createConnextraScript(ConnextraType.HP_RESET_PICKS, this._userStore.user?.id);
	};

	onSavePredictions = () => {
		localStorage.removeItem("predictions");
		localStorage.setItem("predictions", JSON.stringify(this._hpStore.predictions));
	};

	private getRoundPredictions = () => {
		const currentRoundId = this._roundStore.selectedRoundId;
		const roundPredictions = get(this._hpStore.predictions, currentRoundId, []);

		if (roundPredictions) {
			return roundPredictions.map((it) => it.matchId);
		}
	};

	onAutoFill = () => {
		const currentRoundId = this._roundStore.selectedRoundId;

		const roundPredictions = get(this._hpStore.predictions, currentRoundId, []);

		const selectedMatchesIds = this.getRoundPredictions() ?? [];

		const predictionsForSave = this.matchesByRoundId
			.filter((match) => !includes(selectedMatchesIds, match.id)) // Exclude matches that are already predicted
			.map((match) => {
				// Retrieve standings for both home and away squads for the actual round
				const homeSquad = this._standingsStore.lastCompleteStandingsBySquadId(
					match.homeSquadId,
					this._roundStore.actualStandingsRoundId
				);
				const awaySquad = this._standingsStore.lastCompleteStandingsBySquadId(
					match.awaySquadId,
					this._roundStore.actualStandingsRoundId
				);

				if( !homeSquad || !awaySquad ) {
					return {squadId: 0, matchId: 0};
				}

				return {
					squadId: Number(awaySquad.wins) > Number(homeSquad.wins)
						? awaySquad.squadId
						: homeSquad.squadId,
					matchId: match.id,
				};
			});

		const payload = roundPredictions
			? [...roundPredictions, ...predictionsForSave]
			: [...predictionsForSave];

		this._hpStore.setAutoFillPrediction(payload);

		this._hpStore.setIsPredictionsChanged(true);
	}
	;

	onSavePlayoffPicture = () => {
		if (this._userStore.isAuthorized) {
			this._modalsStore.showModal(ModalType.FINAL_SAVE, {
				message: "",
			});
		} else {
			this._modalsStore.showModal(ModalType.LOSE_PICKS, {
				message: "",
			});
		}
	};

	init(param: void) {
		this._hpStore.setIsPredictionsChanged(false);
	}
}
